import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '@ui-nuvertos/shared';

@Injectable({
  providedIn: 'root'
})
export class InvoiceService {
  private accountingUrl = environment.accountingUrl;

  constructor(private http: HttpClient, private cookieService: CookieService) {}

  // List invoices, optional query params (e.g., pagination, filters)
  getInvoices(params?: { [param: string]: string | number }): Observable<any> {
    const url = `${this.accountingUrl}/api/v1/vendor-invoices/`;
    const httpOptions = { params: new HttpParams({ fromObject: params || {} }), withCredentials: true };
    return this.http.get<any>(url, httpOptions);
  }

  // Get single invoice by id
  getInvoiceById(invoiceId: string): Observable<any> {
    const url = `${this.accountingUrl}/api/v1/vendor-invoices/${invoiceId}`;
    return this.http.get<any>(url, { withCredentials: true });
  }

  // Create a new invoice
  createInvoice(invoiceData: any): Observable<any> {
    const url = `${this.accountingUrl}/api/v1/vendor-invoices/`;
    return this.http.post<any>(url, invoiceData, { withCredentials: true });
  }

  // Update an existing invoice
  updateInvoice(invoiceData: any, invoiceId: string): Observable<any> {
    console.log("invoiceId",invoiceId)
    const url = `${this.accountingUrl}/api/v1/vendor-invoices/${invoiceId}`;
    return this.http.put<any>(url, invoiceData, { withCredentials: true });
  }

  // Delete an existing invoice
  deleteInvoice(invoiceId: string | number): Observable<any> {
    const url = `${this.accountingUrl}/api/v1/vendor-invoices/${invoiceId}`;
    return this.http.delete<any>(url, { withCredentials: true });
  }


  getFormTemplates(): Observable<any> {
    const url = `${this.accountingUrl}/api/v1/vendor-invoice-form-templates/`;
    // set cookies
    return this.http.get<any>(url, { withCredentials: true });
  }

  getDefaultFormTemplate(): Observable<any> {
      const url = `${this.accountingUrl}/api/v1/vendor-invoice-form-templates/default`;
      // set cookies
      return this.http.get<any>(url, { withCredentials: true });
  }

  getFormTemplateById(templateId: string): Observable<any> {
      const url = `${this.accountingUrl}/api/v1/vendor-invoice-form-templates/${templateId}`;
      return this.http.get<any>(url, { withCredentials: true });
  }

  // Client Invoice Form Template methods
  getClientInvoiceFormTemplates(): Observable<any> {
    const url = `${this.accountingUrl}/api/v1/client-invoice-form-templates/`;
    return this.http.get<any>(url, { withCredentials: true });
  }

  getDefaultClientInvoiceFormTemplate(): Observable<any> {
    const url = `${this.accountingUrl}/api/v1/client-invoice-form-templates/default`;
    return this.http.get<any>(url, { withCredentials: true });
  }

  getClientInvoiceFormTemplateById(templateId: string): Observable<any> {
    const url = `${this.accountingUrl}/api/v1/client-invoice-form-templates/${templateId}`;
    return this.http.get<any>(url, { withCredentials: true });
  }

  // Fetch POs for a vendor (assumes backend supports vendor_id filter)
  getVendorPurchaseOrders(vendorId: string, params?: { [param: string]: string | string[] }, excludeClosed: boolean = true): Observable<any> {
    const url = `${this.accountingUrl}/api/v1/vendor-pos/`;
    const allParams = { vendor_id: vendorId, exclude_closed: excludeClosed.toString(), ...(params || {}) };
    const httpParams = new HttpParams({ fromObject: allParams });
    return this.http.get<any>(url, { params: httpParams, withCredentials: true });
  }

  // Fetch Purchase Order by id
  getPurchaseOrderById(poId: string): Observable<any> {
    const url = `${this.accountingUrl}/api/v1/vendor-pos/${poId}`;
    return this.http.get<any>(url, { withCredentials: true });
  }

  // Get all clients
  getAllClients(params?: { [param: string]: string | string[] }): Observable<any> {
    const url = `${this.accountingUrl}/api/v1/clients`;
    const httpOptions = { params: new HttpParams({ fromObject: params || {} }), withCredentials: true };
    return this.http.get<any>(url, httpOptions);
  }

  // Get client orders for a specific client
  getClientOrders(clientId: string, params?: { [param: string]: string | string[] }): Observable<any> {
    const url = `${this.accountingUrl}/api/v1/client-orders/`;
    const httpParams = new HttpParams({ fromObject: { client_id: clientId, ...(params || {}) } });
    return this.http.get<any>(url, { params: httpParams, withCredentials: true });
  }

  // Get client order by ID
  getClientOrderById(orderId: string): Observable<any> {
    const url = `${this.accountingUrl}/api/v1/client-orders/${orderId}`;
    return this.http.get<any>(url, { withCredentials: true });
  }

  // Get audit logs for a vendor invoice
  getAuditLogs(invoiceId: string | number, filterParams?: any): Observable<any> {
    let query = '';

    if (filterParams && Object.keys(filterParams).length > 0) {
      query = '?' + new URLSearchParams(filterParams).toString();
    }

    const url = `${this.accountingUrl}/api/v1/vendor-invoices/${invoiceId}/audit-logs${query}`;
    return this.http.get<any>(url, { withCredentials: true });
  }

  // Get GL Impact data for a document
  getGlImpact(documentType: string, documentId: string, includeVoidEntries?: boolean): Observable<any> {
    const url = `${this.accountingUrl}/api/v1/gl-impact`;
    let params = new HttpParams()
      .set('document_type', documentType)
      .set('document_id', documentId);
    
    if (includeVoidEntries !== undefined) {
      params = params.set('include_void_entries', includeVoidEntries.toString());
    }
    
    return this.http.get<any>(url, { params, withCredentials: true });
  }

  // Get payments for a vendor invoice
  getInvoicePayments(invoiceId: string | number): Observable<any> {
    const url = `${this.accountingUrl}/api/v1/vendor-invoices/${invoiceId}/payment`;
    return this.http.get<any>(url, { withCredentials: true });
  }

  // Get print data for a vendor invoice (for PDF generation)
  getPrintData(invoiceId: string | number): Observable<any> {
    const url = `${this.accountingUrl}/api/v1/vendor-invoices/${invoiceId}/print-data`;
    return this.http.get<any>(url, { withCredentials: true });
  }
}
